import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MasterLayout from '../masterLayout/MasterLayout';
import Breadcrumb from '../components/Breadcrumb';
import {
  AppAlert,
  AppButton,
  AppCard,
  AppEmptyState,
  AppFormField,
  AppInput,
  AppSpinner,
} from '../components/ui';
import UserJetonsCard from '../components/admin/UserJetonsCard';
import UserRolesPicker from '../components/admin/UserRolesPicker';
import UserRolesManageModal from '../components/admin/UserRolesManageModal';
import UserActiveToggle from '../components/admin/UserActiveToggle';
import apiService from '../services/apiService';
import { notify } from '../components/common/AppToast';
import { ROUTES } from '../config/routes';
import useUserRoles from '../hooks/useUserRoles';
import '../styles/kkp-planning-page.scss';

const EditUserPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { roles, loading: rolesLoading, reload: reloadRoles } = useUserRoles();
  const [rolesModalOpen, setRolesModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [userNotFound, setUserNotFound] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    roles: [],
    isActive: true
  });

  const [passwordData, setPasswordData] = useState({
    password: '',
    confirmPassword: ''
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [showPasswordFields, setShowPasswordFields] = useState(false);

  useEffect(() => {
    const loadUser = async () => {
      try {
        setLoading(true);
        setError(null);

        const result = await apiService.makeRequest('GET', `/users/${id}`);

        if (result.success) {
          const user = result.data;
          setFormData({
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
            roles: user.roles || [],
            isActive: user.isActive
          });
        } else {
          setUserNotFound(true);
        }
      } catch (err) {
        console.error('Erreur lors du chargement de l\'utilisateur:', err);
        if (err.message.includes('404') || err.message.includes('non trouvé')) {
          setUserNotFound(true);
        } else {
          setError(err.message || 'Erreur lors du chargement de l\'utilisateur');
        }
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadUser();
    }
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;

    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));

    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleRoleChange = (roleValue) => {
    setFormData(prev => {
      const currentRoles = prev.roles;
      const newRoles = currentRoles.includes(roleValue)
        ? currentRoles.filter(role => role !== roleValue)
        : [...currentRoles, roleValue];

      return {
        ...prev,
        roles: newRoles
      };
    });

    if (validationErrors.roles) {
      setValidationErrors(prev => ({
        ...prev,
        roles: null
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.email) {
      errors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'L\'email n\'est pas valide';
    }

    if (!formData.firstName.trim()) {
      errors.firstName = 'Le prénom est requis';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Le nom est requis';
    }

    if (formData.roles.length === 0) {
      errors.roles = 'Au moins un rôle doit être sélectionné';
    }

    if (showPasswordFields) {
      if (passwordData.password && passwordData.password.length < 6) {
        errors.password = 'Le mot de passe doit contenir au moins 6 caractères';
      }

      if (passwordData.password !== passwordData.confirmPassword) {
        errors.confirmPassword = 'Les mots de passe ne correspondent pas';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const updateData = { ...formData };

      if (showPasswordFields && passwordData.password) {
        updateData.password = passwordData.password;
      }

      const result = await apiService.makeRequest('PUT', `/users/${id}`, updateData);

      if (result.success) {
        notify.success('Utilisateur mis à jour — redirection…');
        setTimeout(() => {
          navigate(ROUTES.admin.users);
        }, 2000);
      } else {
        const msg = result.message || 'Erreur lors de la mise à jour de l\'utilisateur';
        setError(msg);
        notify.error(msg);
      }
    } catch (err) {
      console.error('Erreur lors de la mise à jour de l\'utilisateur:', err);
      const msg = err.message || 'Erreur lors de la mise à jour de l\'utilisateur';
      setError(msg);
      notify.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(ROUTES.admin.users);
  };

  const togglePasswordFields = () => {
    setShowPasswordFields(!showPasswordFields);
    setPasswordData({ password: '', confirmPassword: '' });
    setValidationErrors(prev => ({
      ...prev,
      password: null,
      confirmPassword: null
    }));
  };

  if (loading) {
    return (
      <MasterLayout>
        <Breadcrumb title="Modifier l'utilisateur" subtitle="Chargement…" />
        <AppCard>
          <AppSpinner />
          <p className="mt-3 mb-0 text-center text-secondary-light">Chargement de l&apos;utilisateur…</p>
        </AppCard>
      </MasterLayout>
    );
  }

  if (userNotFound) {
    return (
      <MasterLayout>
        <Breadcrumb title="Modifier l'utilisateur" subtitle="Utilisateur introuvable" />
        <AppCard>
          <AppEmptyState
            icon="mdi:account-off-outline"
            message="L'utilisateur demandé n'existe pas ou a été supprimé."
            actions={[{
              key: 'back',
              label: 'Retour à la liste',
              variant: 'primary',
              icon: 'mdi:arrow-left',
              onClick: () => navigate(ROUTES.admin.users),
            }]}
          />
        </AppCard>
      </MasterLayout>
    );
  }

  return (
    <MasterLayout>
      <Breadcrumb
        title="Modifier l'utilisateur"
        subtitle={`Modifier les informations de ${formData.firstName} ${formData.lastName}`}
        actions={(
          <AppButton size="sm" variant="outline-secondary" icon="mdi:arrow-left" onClick={handleCancel}>
            Retour
          </AppButton>
        )}
      />

      {error && (
        <AppAlert>{error}</AppAlert>
      )}

      <div className="row">
        <div className="col-12">
          <AppCard className="kkp-planning-results-card" title="Informations de l'utilisateur">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <AppFormField className="col-md-6 mb-3" label="Email" htmlFor="email" required error={validationErrors.email}>
                  <AppInput
                    type="email"
                    className={validationErrors.email ? 'is-invalid' : ''}
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="exemple@email.com"
                    disabled={saving}
                  />
                </AppFormField>

                <AppFormField className="col-md-6 mb-3" label="Prénom" htmlFor="firstName" required error={validationErrors.firstName}>
                  <AppInput
                    type="text"
                    className={validationErrors.firstName ? 'is-invalid' : ''}
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="Prénom"
                    disabled={saving}
                  />
                </AppFormField>

                <AppFormField className="col-md-6 mb-3" label="Nom" htmlFor="lastName" required error={validationErrors.lastName}>
                  <AppInput
                    type="text"
                    className={validationErrors.lastName ? 'is-invalid' : ''}
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Nom"
                    disabled={saving}
                  />
                </AppFormField>

                <AppFormField className="col-12 mb-3" label="Rôles" required error={validationErrors.roles}>
                  <UserRolesPicker
                    roles={roles}
                    selected={formData.roles}
                    onToggle={handleRoleChange}
                    disabled={saving || rolesLoading}
                    onCreate={() => setRolesModalOpen(true)}
                  />
                </AppFormField>

                <AppFormField className="col-12 mb-3" label="Statut du compte">
                  <UserActiveToggle
                    active={formData.isActive}
                    onChange={(isActive) => setFormData((prev) => ({ ...prev, isActive }))}
                    disabled={saving}
                  />
                </AppFormField>

                <div className="col-12 mb-3">
                  <div className="card border">
                    <div className="card-header bg-light">
                      <div className="d-flex justify-content-between align-items-center">
                        <h6 className="mb-0">Changer le mot de passe</h6>
                        <AppButton
                          size="sm"
                          variant="outline-primary"
                          onClick={togglePasswordFields}
                          disabled={saving}
                        >
                          {showPasswordFields ? 'Masquer' : 'Modifier le mot de passe'}
                        </AppButton>
                      </div>
                    </div>
                    {showPasswordFields && (
                      <div className="card-body">
                        <div className="row">
                          <AppFormField className="col-md-6 mb-3" label="Nouveau mot de passe" htmlFor="password" error={validationErrors.password}>
                            <AppInput
                              type="password"
                              className={validationErrors.password ? 'is-invalid' : ''}
                              id="password"
                              name="password"
                              value={passwordData.password}
                              onChange={handlePasswordChange}
                              placeholder="Nouveau mot de passe"
                              disabled={saving}
                            />
                          </AppFormField>
                          <AppFormField className="col-md-6 mb-3" label="Confirmer le mot de passe" htmlFor="confirmPassword" error={validationErrors.confirmPassword}>
                            <AppInput
                              type="password"
                              className={validationErrors.confirmPassword ? 'is-invalid' : ''}
                              id="confirmPassword"
                              name="confirmPassword"
                              value={passwordData.confirmPassword}
                              onChange={handlePasswordChange}
                              placeholder="Confirmer le mot de passe"
                              disabled={saving}
                            />
                          </AppFormField>
                        </div>
                        <small className="text-muted">
                          Laissez vide pour conserver le mot de passe actuel
                        </small>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="d-flex gap-2 justify-content-end">
                <AppButton variant="outline-secondary" onClick={handleCancel} disabled={saving}>
                  Annuler
                </AppButton>
                <AppButton
                  type="submit"
                  variant="success"
                  icon="mdi:content-save-outline"
                  loading={saving}
                  loadingLabel="Sauvegarde..."
                >
                  Sauvegarder
                </AppButton>
              </div>
            </form>
          </AppCard>
          <UserJetonsCard userId={id} />
        </div>
      </div>
      <UserRolesManageModal
        show={rolesModalOpen}
        roles={roles}
        loading={rolesLoading}
        onClose={() => setRolesModalOpen(false)}
        onChanged={async (created) => {
          await reloadRoles();
          if (created?.value) {
            setFormData((prev) => (
              prev.roles.includes(created.value)
                ? prev
                : { ...prev, roles: [...prev.roles, created.value] }
            ));
            setRolesModalOpen(false);
          }
        }}
      />
    </MasterLayout>
  );
};

export default EditUserPage;
